// content.ts — copy oficial da UdiSchool (port literal de content.js do design).
// Travessões já removidos no original. NÃO ALTERAR sem aprovação do cliente.

export type DorBlock = {
  head: string;
  items: Array<[string, string]>;
};

export type Trilha = {
  key: string;
  flag: string | null;
  promise: string;
  who: string;
  gap: string;
  situations: string;
  format: string;
  level: string;
  cta: string;
};

export type ResultadoCard = {
  focus: string;
  impact: string;
  text: string;
  apply: string;
};

export type P3QuestionKind = "text" | "choice" | "scale";

export type P3Question =
  | [text: string, hint: string, kind: "text"]
  | [text: string, hint: string, kind: "choice", options: string[]]
  | [text: string, hint: string, kind: "scale"];

export const CONTENT = {
  brand: {
    name: "UdiSchool",
    tagline:
      "inglês de verdade para quem quer avançar nos estudos, na carreira e na vida.",
    whatsapp: "34 99896-1974",
    whatsappUrl: "https://wa.me/5534998961974",
  },

  hero: {
    eyebrow: "Treinamento de inglês · Uberlândia",
    headline: "Você já estudou inglês, mas ainda trava na hora de falar?",
    subhead:
      "Descubra o que está impedindo você de falar inglês com confiança e receba um plano para avançar.",
    institutional: [
      "O mercado está cada vez mais internacionalizado. Empresas, universidades e oportunidades exigem profissionais capazes de se comunicar em inglês com segurança.",
      "Foi para atender essa necessidade que nasceu a UdiSchool, uma plataforma de treinamento de inglês para jovens e adultos que querem avançar nos estudos, crescer profissionalmente e ampliar suas oportunidades no Brasil e no exterior.",
      "Com aulas online ao vivo, acompanhamento próximo e prática constante, ajudamos nossos alunos a transformar conhecimento em comunicação real.",
    ],
    authority:
      "Aprenda inglês com um professor que já trabalhou, estudou e viveu em ambientes internacionais reais, e que entende os desafios de adultos que precisam usar o idioma para crescer na carreira e nos estudos.",
    authority2:
      "A UdiSchool é conduzida por Alexandre Sousa, doutorando e professor com mais de 15 anos de experiência, certificado pelo Montgomery College, em Maryland (EUA), onde morou por dois anos e atuou como professor de inglês para imigrantes de mais de 50 países.",
    cta: "Agendar minha Avaliação Gratuita",
    ctaNote: "",
  },

  dor: {
    label: "A urgência",
    title:
      "Quantas boas oportunidades você ainda está disposto a perder por não falar inglês?",
    intro: [
      "Boas oportunidades surgem todos os dias, e tendem a premiar quem está mais preparado.",
      "Por isso, o inglês deixou de ser apenas um diferencial. Para estudantes e profissionais de hoje, dominar uma das línguas mais importantes do mundo tornou-se uma habilidade decisiva.",
      "Quanto antes você desenvolver essa competência, maiores serão suas chances de estudar melhor, crescer na carreira, viajar com mais autonomia e conquistar novas oportunidades.",
    ],
    blockTitle:
      "O que você deixa de conquistar quando o inglês não acompanha suas ambições de futuro?",
    student: {
      head: "Se você é estudante",
      items: [
        [
          "Vanguarda do conhecimento",
          "Fica refém de traduções eletrônicas, não compreende plenamente os textos de referência e acompanha com atraso as ideias que estão circulando no mundo.",
        ],
        [
          "Tempo e eficiência",
          "Desperdiça horas preciosas tentando decifrar textos, atrasando leituras, trabalhos, pesquisas e sua produção acadêmica.",
        ],
        [
          "Bolsas e intercâmbios",
          "Perde oportunidades em processos seletivos, programas internacionais e a chance de estudar em universidades de excelência ao redor do mundo.",
        ],
        [
          "Networking global",
          "Deixa de interagir com estudantes, professores e pesquisadores de outros países, limitando suas conexões acadêmicas e culturais.",
        ],
        [
          "A largada da carreira",
          "Entra no mercado com menos preparo e começa a disputa profissional em desvantagem diante de quem já utiliza o inglês com segurança.",
        ],
      ],
    } satisfies DorBlock,
    professional: {
      head: "Se você é profissional",
      items: [
        [
          "Vagas mais disputadas",
          "Pode ser eliminado logo nas primeiras etapas de processos seletivos para multinacionais, grandes empresas e organizações com atuação internacional.",
        ],
        [
          "Promoções e liderança",
          "Perde espaço para profissionais que possuem formação semelhante à sua, mas conseguem se comunicar em inglês.",
        ],
        [
          "Renda e crescimento",
          "Enquanto profissionais bilíngues acessam mercados mais amplos, você permanece limitado às oportunidades disponíveis apenas em português.",
        ],
        [
          "Networking estratégico",
          "Deixa de participar de conversas, negociações, parcerias e oportunidades de negócios com pessoas e empresas de outros países.",
        ],
        [
          "Autoridade profissional",
          "Sua experiência perde força quando o idioma muda, impedindo que você demonstre todo o conhecimento e a capacidade que possui.",
        ],
      ],
    } satisfies DorBlock,
    closing: [
      "A boa notícia é que isso pode mudar.",
      "Mas toda transformação começa com um diagnóstico honesto.",
      "Antes de avançar, você precisa saber exatamente qual é o seu nível atual, quais são seus pontos fortes, quais habilidades precisam ser desenvolvidas e qual caminho faz mais sentido para os seus objetivos.",
    ],
    cta: "Quero fazer minha Avaliação Diagnóstica",
  },

  metodo: {
    label: "Por que a UdiSchool",
    title: "Por que a UdiSchool?",
    intro: [
      "A UdiSchool é uma plataforma de treinamento em inglês voltada para jovens e adultos que precisam transformar estudo em comunicação real.",
      "Nossa metodologia COMPASS, a 'bússola' em inglês, orienta sua jornada no idioma: identifica seu ponto de partida, ajuda a definir seus objetivos e organiza o caminho para você avançar com clareza e consistência.",
      "Com método, prática ativa, acompanhamento próximo e contato frequente com o idioma, preparamos você para usar o inglês em situações concretas da vida acadêmica, profissional e pessoal: reuniões, entrevistas, apresentações, congressos, viagens, estudos e conversas com pessoas de outros países.",
    ],
    pillarsIntro: "Nossa metodologia é organizada em sete pilares.",
    pillarsTitle: "Os sete pilares da metodologia COMPASS",
    pillars: [
      [
        "C",
        "Contact",
        "Contato frequente e consistente com o idioma para que o inglês deixe de estar presente apenas na hora da aula e passe a fazer parte da sua rotina de aprendizagem.",
      ],
      [
        "O",
        "Orientation",
        "Direcionamento claro por meio de um plano de estudos que considera seu ponto de partida, seus objetivos e o caminho necessário para avançar.",
      ],
      [
        "M",
        "Meaningful Input",
        "Conteúdos relevantes e conectados à vida adulta, aos estudos, ao trabalho, à cultura e aos temas que realmente fazem sentido para você.",
      ],
      [
        "P",
        "Production",
        "Prática ativa desde o início. Em cada encontro, você fala, escreve, responde, pergunta, constrói frases e aprende usando o idioma.",
      ],
      [
        "A",
        "Assistance",
        "Acompanhamento próximo do professor, com correções, orientações e feedback para que você saiba o que melhorar e como continuar evoluindo.",
      ],
      [
        "S",
        "Shared Practice",
        "Treinamento em grupos reduzidos, com interação entre alunos, troca de experiências e práticas em grupo para ganhar confiança na comunicação.",
      ],
      [
        "S",
        "Skills for Life",
        "Desenvolvimento do inglês para situações reais da vida adulta: reuniões, entrevistas, apresentações, viagens, estudos, networking e participação em ambientes internacionais.",
      ],
    ] as Array<[string, string, string]>,
  },

  trilhas: {
    label: "As trilhas UdiSchool",
    title: "Escolha o caminho certo para o seu inglês",
    intro: [
      "A UdiSchool oferece treinamentos organizados em trilhas para ajudar você a avançar com mais direcionamento, de acordo com seu nível atual e seus objetivos.",
      "Seja para destravar a fala, crescer no mercado de trabalho ou desenvolver o inglês acadêmico, você conta com orientação especializada desde o primeiro contato.",
      "Antes da matrícula, nossa Avaliação Diagnóstica identifica seu nível atual, as habilidades que você precisa desenvolver e a trilha, ou combinação de trilhas, mais adequada para o seu momento.",
      "A partir desse diagnóstico, você recebe uma orientação clara sobre o caminho recomendado, seja em uma turma aberta, em um treinamento sob demanda ou em uma solução personalizada para seus objetivos.",
    ],
    blockTitle: "As trilhas de treinamento",
    cards: [
      {
        key: "DESTRAVA",
        flag: "Trilha recomendada para a Turma Pioneira",
        promise: "Para quem já estudou inglês, mas ainda trava na hora de falar.",
        who: "Jovens e adultos que já tiveram contato com o idioma, mas sentem insegurança, falta de prática e dificuldade para se comunicar no dia a dia.",
        gap: "Bloqueio na expressão oral, dificuldade de compreensão auditiva e falta de ritmo de conversação.",
        situations: "Conversas reais, apresentações simples, viagens e interações cotidianas.",
        format: "Grupos reduzidos, treinamento online ao vivo, prática ativa e feedback direcionado.",
        level: "A partir do básico consolidado.",
        cta: "Quero descobrir se o DESTRAVA é para mim",
      },
      {
        key: "WORK",
        flag: null,
        promise:
          "Inglês profissional para reuniões, entrevistas de emprego, apresentações e comunicação no ambiente de trabalho.",
        who: "Profissionais, gestores e líderes que usam ou desejam usar o inglês no trabalho, em processos seletivos, negócios ou ambientes internacionais.",
        gap: "Perda de oportunidades de crescimento, insegurança em reuniões e dificuldade de se posicionar profissionalmente em inglês.",
        situations:
          "Reuniões, apresentações corporativas, entrevistas de emprego, e-mails, conversas profissionais e negociações do dia a dia.",
        format: "Treinamento aplicado, simulações de situações profissionais e feedback direcionado.",
        level: "Intermediário.",
        cta: "Quero saber se o WORK é o meu caminho",
      },
      {
        key: "ACADEMY",
        flag: null,
        promise:
          "Inglês acadêmico para estudantes, pesquisadores e pós-graduandos que precisam ler, escrever, apresentar e participar de debates em inglês.",
        who: "Universitários, professores, pesquisadores e pós-graduandos interessados em ampliar sua atuação acadêmica em ambientes internacionais.",
        gap: "Dependência de tradução, dificuldade com compreensão leitora e expressão escrita em inglês, além de insegurança para participar de apresentações, congressos e debates.",
        situations:
          "Leitura de artigos científicos, escrita acadêmica, apresentações em congressos, participação em eventos internacionais e interação com pesquisadores de outros países.",
        format:
          "Orientação para leitura, escrita e discussão de textos acadêmicos, com vocabulário estratégico e prática aplicada.",
        level: "Intermediário.",
        cta: "Quero saber se o ACADEMY é indicado para mim",
      },
    ] satisfies Trilha[],
    closing: [
      "Você não precisa escolher sua trilha sozinho.",
      "A Avaliação Diagnóstica ajuda a identificar seu nível atual, seus objetivos e o caminho mais adequado para sua evolução no inglês.",
    ],
    cta: "Quero fazer minha Avaliação Diagnóstica",
  },

  professor: {
    label: "Quem conduz o treinamento",
    title: "Quem conduz o seu treinamento?",
    subtitle: "Conheça o Prof. Alexandre Sousa, fundador da UdiSchool",
    international: [
      "Alexandre Sousa viveu, estudou e trabalhou em ambientes internacionais, inclusive nos Estados Unidos, onde lecionou inglês para alunos de mais de 50 países e aprofundou sua formação em ensino de idiomas.",
      "É certificado em TESOL (Teaching English to Speakers of Other Languages) pelo Montgomery College, em Maryland, e atuou em instituições de destaque, incluindo o Washington English Center, em Washington, D.C.",
      "Ao longo de sua trajetória, transformou essa experiência internacional em uma metodologia voltada para brasileiros que precisam usar o inglês com mais clareza, segurança e autonomia em situações reais do dia a dia.",
    ],
    formation: [
      "Além da atuação no ensino do idioma, sua trajetória reúne formação acadêmica, experiência internacional e vivência profissional em áreas conectadas ao mundo global.",
      "É economista, Mestre em Relações Internacionais pela Universidade de São Paulo (USP) e doutorando em Relações Internacionais pela Universidade Federal de Uberlândia (UFU).",
      "Sua experiência passa por comércio exterior, logística internacional, segurança e defesa, gestão de riscos e organismos internacionais. Essa combinação permite conectar o ensino do inglês às demandas concretas da vida acadêmica, profissional e internacional.",
    ],
    philosophy: [
      "A UdiSchool nasceu da convicção de que adultos aprendem melhor quando conseguem conectar o idioma aos seus objetivos reais de estudo, trabalho, carreira e desenvolvimento pessoal.",
      "Por isso, Alexandre atua não apenas como professor, mas também como orientador e mentor da jornada de aprendizagem de seus alunos.",
      "O objetivo não é apenas ensinar estruturas gramaticais ou ampliar vocabulário. É desenvolver a capacidade de compreender, comunicar, argumentar, apresentar ideias e atuar com mais confiança em ambientes nacionais e internacionais.",
    ],
    badges: [
      [
        "Habilitação Internacional",
        "Certificação TESOL nos Estados Unidos e experiência docente com alunos de mais de 50 países.",
      ],
      [
        "Visão de Mercado Global",
        "Experiência em comércio exterior, logística internacional, segurança e defesa, gestão de riscos e organismos internacionais.",
      ],
      [
        "Excelência Acadêmica",
        "Economista, Mestre em Relações Internacionais pela USP e Doutorando em Relações Internacionais pela UFU.",
      ],
      [
        "Ensino Aplicado à Vida Real",
        "Inglês conectado às necessidades da carreira, dos estudos e da comunicação em ambientes internacionais.",
      ],
    ] as Array<[string, string]>,
    cta: "Quero fazer minha Avaliação Diagnóstica",
  },

  resultados: {
    label: "Resultados que buscamos construir",
    title: "O que você pode desenvolver com a UdiSchool",
    intro: [
      "A metodologia que fundamenta a UdiSchool é resultado de anos de ensino, orientação e acompanhamento de jovens e adultos em diferentes contextos de aprendizagem.",
      "Nosso foco é ajudar você a superar bloqueios, desenvolver autonomia e transformar o inglês em uma ferramenta real para seus estudos, sua carreira e sua vida.",
    ],
    blockTitle: "Situações que o treinamento ajuda a superar",
    cards: [
      {
        focus: "Foco em Carreira e Mercado",
        impact: "Participar melhor de entrevistas, reuniões e processos seletivos em inglês.",
        text: "Se você tem conhecimento técnico, mas trava quando precisa se comunicar em inglês, o treinamento ajuda a organizar ideias, ganhar segurança e praticar situações reais do ambiente profissional.",
        apply: "Trilha WORK · Inglês profissional",
      },
      {
        focus: "Foco em Estudos e Academia",
        impact: "Ler, escrever, apresentar e participar melhor da vida acadêmica em inglês.",
        text: "Se seu desafio é lidar com artigos, textos científicos, apresentações, congressos ou debates acadêmicos, o treinamento ajuda a desenvolver compreensão leitora, expressão escrita, expressão oral e vocabulário acadêmico aplicado.",
        apply: "Trilha ACADEMY · Inglês acadêmico",
      },
      {
        focus: "Foco em Autonomia e Fluência",
        impact: "Perder o medo de falar e usar o inglês com mais naturalidade no dia a dia.",
        text: "Se você já estudou inglês, mas ainda sente vergonha, insegurança ou falta de ritmo para conversar, o treinamento ajuda a desenvolver prática ativa, compreensão auditiva e expressão oral em situações reais.",
        apply: "Trilha DESTRAVA · Conversação e confiança",
      },
    ] satisfies ResultadoCard[],
    closing: [
      "O próximo passo começa com um diagnóstico claro do seu momento atual.",
      "A partir dele, você entende quais habilidades precisa desenvolver e qual trilha, ou combinação de trilhas, faz mais sentido para seus objetivos.",
    ],
    cta: "Quero fazer minha Avaliação Diagnóstica",
  },

  diagnostico: {
    label: "Como funciona a Avaliação",
    title: "Como funciona a Avaliação Diagnóstica?",
    intro: [
      "Uma sessão 100% gratuita, sem compromisso e com horário previamente agendado.",
      "Criada para mapear, de forma técnica e objetiva, seu desempenho real e prático no inglês."
    ],
    blockTitle: "As quatro etapas da avaliação",
    steps: [
      [
        "Mapeamento Prático",
        "Avaliamos o seu desempenho em situações da vida real. Uma visão completa, sem diagnósticos superficiais baseados apenas em gramática isolada.",
      ],
      [
        "Dinâmica de Conversação",
        "Inspirada em exames internacionais, essa etapa permite observar sua interação, ritmo de fala, vocabulário e segurança para se comunicar.",
      ],
      [
        "Identificação de Gargalos",
        "Mais do que medir nível, identificamos o que impede seu avanço: falta de vocabulário, insegurança para falar ou ausência de rotina.",
      ],
      [
        "Seu Plano Direcionado",
        "Com o resultado em mãos, você não escolhe uma turma no escuro. Recebe um plano de treinamento 100% alinhado aos seus objetivos.",
      ],
    ] as Array<[string, string]>,
    closing:
      "Garanta sua vaga em uma das sessões gratuitas preenchendo o formulário de aplicação.",
    cta: "Agendar minha Avaliação",
  },

  faq: {
    label: "Perguntas frequentes",
    title: "Perguntas Frequentes",
    intro:
      "Ainda tem alguma dúvida? Veja abaixo as respostas para os principais questionamentos sobre os treinamentos, a Avaliação Diagnóstica e a dinâmica da UdiSchool.",
    items: [
      [
        "A UdiSchool é uma escola de inglês tradicional?",
        [
          "Não. A UdiSchool é uma plataforma de treinamento em inglês voltada para jovens e adultos que precisam desenvolver comunicação real para trabalho, estudos, viagens e vida internacional.",
          "Os treinamentos são organizados em trilhas direcionadas (DESTRAVA, WORK e ACADEMY) e seguem a metodologia COMPASS, que prioriza contato frequente com o idioma, produção ativa do aluno, prática acompanhada e feedback próximo.",
        ],
      ],
      [
        "Como funcionam os encontros? As aulas são gravadas ou ao vivo?",
        [
          "Os encontros principais são online e ao vivo, conduzidos em grupos reduzidos. Essa estrutura permite práticas em grupo, interação real e acompanhamento do professor em tempo real.",
          "A UdiSchool não trabalha com um modelo em que o aluno apenas assiste a aulas gravadas sozinho. O foco é participação ativa, produção em inglês e evolução acompanhada.",
        ],
      ],
      [
        "Eu preciso ter nível avançado para entrar na UdiSchool?",
        [
          "Não. A UdiSchool atende diferentes perfis de jovens e adultos.",
          "A trilha DESTRAVA é indicada para quem já teve contato com o inglês, mas ainda sente insegurança para falar. As trilhas WORK e ACADEMY são voltadas para quem precisa desenvolver o idioma em contextos profissionais, acadêmicos ou estratégicos.",
          "A Avaliação Diagnóstica existe justamente para identificar seu momento atual, as habilidades que você precisa desenvolver e o caminho mais adequado para seus objetivos.",
        ],
      ],
      [
        "Qual é a duração dos treinamentos?",
        [
          "A duração depende da trilha indicada e do seu plano de desenvolvimento.",
          "A Turma Pioneira DESTRAVA 90, por exemplo, foi desenhada como um ciclo inicial de 12 semanas.",
          "Mais importante do que prometer um prazo artificial é criar uma rotina consistente de prática, produção e feedback. A evolução acontece quando o aluno participa dos encontros, mantém contato frequente com o idioma e segue o plano indicado.",
        ],
      ],
      [
        "Como funciona a Avaliação Diagnóstica? Ela é realmente gratuita?",
        [
          "Sim. A Avaliação Diagnóstica é gratuita e sem compromisso.",
          "Ela é realizada em sessões agendadas e avalia seu desempenho nas quatro habilidades essenciais do inglês: compreensão leitora, compreensão auditiva, expressão oral e expressão escrita.",
          "Ao final do processo, você recebe uma orientação sobre seu momento atual, as habilidades que precisa desenvolver e a trilha, ou combinação de trilhas, mais adequada aos seus objetivos.",
        ],
      ],
      [
        "Moro fora do Triângulo Mineiro. Posso me matricular?",
        [
          "Sim. A UdiSchool nasceu no Triângulo Mineiro e carrega essa identidade regional, mas sua estrutura é online.",
          "Isso permite atender estudantes, profissionais e pesquisadores de qualquer localização que busquem desenvolver o inglês da vida real para circular melhor no trabalho, nos estudos, em viagens e em ambientes internacionais.",
        ],
      ],
    ] as Array<[string, string[]]>,
    closing:
      "Sua dúvida não apareceu aqui? A UdiSchool pode ajudar você a entender qual é o melhor caminho para o seu inglês. Você pode agendar sua Avaliação Diagnóstica ou falar conosco diretamente pelo WhatsApp.",
    cta: "Quero agendar minha Avaliação Diagnóstica",
    ctaWhats: "Falar pelo WhatsApp e tirar minhas dúvidas",
  },

  progress: ["Seus dados", "Sua história", "Sua agenda"] as const,

  p2: {
    step: "Etapa 1 de 3",
    title: "Como podemos falar com você?",
    open: "Você está a poucos passos de agendar sua Avaliação Diagnóstica.",
    explain:
      "Antes disso, precisamos apenas de algumas informações básicas para enviar as orientações, o link da AD e, posteriormente, seu Mapa Pessoal de Aprendizagem.",
    fields: ["Nome", "E-mail", "WhatsApp"] as const,
    obs: "campos obrigatórios",
    reinforce:
      "Seus dados serão utilizados exclusivamente para contato relacionado à sua Avaliação Diagnóstica e aos serviços da UdiSchool.",
    button: "Continuar",
    after:
      "Próxima etapa: conte um pouco da sua história com o inglês para que possamos entender seus objetivos, suas dificuldades e como podemos ajudar você a avançar.",
  },

  p3: {
    step: "Etapa 2 de 3",
    title: "Conte sua história com o inglês",
    open:
      "Antes de agendarmos sua Avaliação Diagnóstica, queremos entender um pouco melhor sua trajetória.",
    reinforce: "Não existem respostas certas ou erradas.",
    explain:
      "Quanto mais detalhes você compartilhar, mais preciso será seu diagnóstico e mais personalizado poderá ser seu Mapa Pessoal de Aprendizagem.",
    guidance: "Reserve alguns minutos e responda com sinceridade.",
    questions: [
      [
        "Conte um pouco da sua história com o inglês.",
        "Por exemplo: Onde você estudou? Por quanto tempo? O que funcionou? O que não funcionou?",
        "text",
      ],
      ["Qual é hoje sua maior dificuldade com o inglês?", "Conte com suas próprias palavras.", "text"],
      [
        "Em quais situações você mais sente falta do inglês?",
        "Por exemplo: trabalho, entrevistas, reuniões, estudos, viagens, pesquisa, tecnologia, internet ou outro.",
        "text",
      ],
      [
        "O que você sente quando precisa falar inglês?",
        "Por exemplo: vergonha, insegurança, medo de errar, ansiedade, travamento ou outro.",
        "text",
      ],
      [
        "Você acredita que já perdeu oportunidades por não falar inglês com segurança?",
        "Se sim, conte brevemente quais.",
        "text",
      ],
      [
        "O que você gostaria de conseguir fazer em inglês nos próximos 3 meses?",
        "Seja específico.",
        "text",
      ],
      [
        "E onde você gostaria de estar com seu inglês daqui a 12 meses?",
        "Descreva o resultado ideal.",
        "text",
      ],
      [
        "O que você não quer repetir em um curso de inglês?",
        "Por exemplo: muita teoria, pouca prática, turmas grandes, falta de feedback, falta de direção ou outro.",
        "text",
      ],
      ["Quanto tempo você acredita que consegue dedicar ao inglês fora das aulas?", "", "text"],
      [
        "Qual programa parece mais adequado para você neste momento?",
        "",
        "choice",
        ["DESTRAVA", "WORK", "ACADEMY", "Ainda não sei"],
      ],
      ["Por que você escolheu essa opção?", "", "text"],
      [
        "O que mais chamou sua atenção na UdiSchool?",
        "Pode ser uma ideia, uma promessa, um diferencial, uma mensagem, uma característica do curso ou qualquer outro aspecto que tenha despertado seu interesse.",
        "text",
      ],
      [
        "Caso encontrasse um programa que realmente fizesse sentido para seus objetivos, qual faixa de investimento mensal você consideraria razoável para o desenvolvimento do seu inglês?",
        "",
        "choice",
        [
          "Até R$ 300",
          "R$ 300 a R$ 500",
          "R$ 500 a R$ 800",
          "R$ 800 a R$ 1.500",
          "Acima de R$ 1.500",
          "Prefiro conversar sobre isso depois da Avaliação Diagnóstica",
        ],
      ],
      [
        "O que mais pode dificultar seu avanço no inglês neste momento?",
        "",
        "choice",
        [
          "Falta de tempo",
          "Falta de rotina",
          "Falta de confiança para falar",
          "Não encontrar um curso adequado",
          "Questões financeiras",
          "Outro",
        ],
      ],
      [
        "Em uma escala de 1 a 10, qual é a prioridade de desenvolver seu inglês neste momento?",
        "1 = baixa prioridade · 10 = prioridade máxima.",
        "scale",
      ],
    ] as P3Question[],
    thanks: "Obrigado por compartilhar sua história.",
    transition: "Agora vamos escolher o melhor horário para sua Avaliação Diagnóstica.",
    button: "Agendar",
  },

  p4: {
    step: "Etapa 3 de 3",
    title: "Escolha seu horário",
    open:
      "Obrigado pelas informações. Sua primeira etapa já foi concluída. Agora é hora de agendar sua Avaliação Diagnóstica.",
    whatTitle: "O que acontece na Avaliação Diagnóstica?",
    whatText:
      "A AD é realizada online e tem duração aproximada de 45 a 60 minutos. Ela é composta por quatro etapas, nas quais se avaliam as quatro habilidades da língua inglesa:",
    skills: [
      ["Listening", "compreensão auditiva"],
      ["Reading", "compreensão leitora"],
      ["Speaking", "expressão oral"],
      ["Writing", "expressão escrita"],
    ] as Array<[string, string]>,
    deliverTitle: "O que você recebe ao final?",
    deliver: [
      "Identificação do seu nível atual",
      "Diagnóstico das quatro habilidades",
      "NPI · Nível de Proficiência em Inglês",
      "Mapa Pessoal de Aprendizagem",
      "Recomendações sobre o melhor caminho para alcançar seus objetivos",
    ],
    slotsTitle: "Escolha a melhor data e horário",
    window: "7 a 20 de junho",
    button: "Confirmar agendamento",
    after:
      "Após a confirmação, você receberá as informações da sua Avaliação Diagnóstica por e-mail e WhatsApp.",
    confirmTitle: "Agendamento confirmado!",
    confirmText:
      "Você receberá as informações da sua Avaliação Diagnóstica por e-mail e WhatsApp.",
    confirmFinal: "Fique atento à sua caixa de entrada. Até breve!",
  },

  p5: {
    title: "Obrigado! Sua solicitação foi recebida.",
    subtitle: "Você deu o primeiro passo para descobrir o melhor caminho para o seu inglês.",
    confirm:
      "O link da sua Avaliação Diagnóstica será enviado para o e-mail e para o WhatsApp cadastrados.",
    reinforce:
      "A avaliação é gratuita, online e sem compromisso. Ela vai ajudar a identificar seu nível atual, seus principais bloqueios e a trilha mais adequada para você.",
    next: "Fique atento ao seu e-mail e ao seu WhatsApp para receber as orientações.",
    trust: ["Aulas online e ao vivo", "Grupos reduzidos", "Acompanhamento do professor"],
    ctaWhats: "Falar com a equipe pelo WhatsApp: 34 99896-1974",
    ctaBack: "Voltar para a página inicial",
    final:
      "UdiSchool · inglês de verdade para quem quer avançar nos estudos, na carreira e na vida.",
  },
} as const;

export type Content = typeof CONTENT;
